/**
 * THE CREATE-FORM TABLE — every form the support assistant is allowed to FILL IN.
 *
 * WHAT A PREFILL IS, AND WHAT IT IS NOT
 * ------------------------------------
 * A settings chip does exactly two things: it records the VALUES on
 * `services/supportSettingsBus` and it navigates to the screen that hosts the form. A hook on
 * that page then presses the page's own **Add** button and puts the values in the boxes.
 * **NOTHING HERE SAVES ANYTHING.** The record does not exist until the manager reads the form
 * and presses **Update** — which is also why the panel says so in the thread the moment the
 * chip is used. That is the whole product rule, and it is why this table names *forms* rather
 * than endpoints.
 *
 * ⚠️ THIS IS A SIBLING OF `utility/supportActions.ts`, NOT AN EXTENSION OF IT.
 * An action chip opens a MODAL on the production order screen and carries one production order.
 * A settings chip fills a CREATE FORM on a settings screen and carries several fields. Merging
 * them would mean one table whose central promise is "opens a dialog, except when it fills a
 * form", and one intent type that sometimes has a job number and sometimes has a dictionary —
 * which is exactly the kind of "except when" that goes wrong in the branch nobody reads. Two
 * files, two buses, two rules.
 *
 * ⚠️ THIS FILE IS HALF OF A CROSS-REPO CONTRACT.
 * The Function App holds the other half in `Support/answer-actions.json`, and it holds the
 * FIELD SCHEMA as well as the keys — a field name that is not declared there never crosses the
 * wire, and a value is trimmed, capped and refused if it carries `[`, `]` or a line break
 * before it ever gets here. `supportSettings.test.ts` restates the FA's key and field lists and
 * fails if the two drift, the same way `routes.ts` is pinned for screens. See
 * `AnswerActionGuard.cs`.
 *
 * ⚠️ AND YES, THE MODEL WROTE THESE VALUES. That is correct here and it is the obvious
 * objection, so it is answered in the same place on both sides: a data card's fields are a
 * CLAIM ABOUT THE SYSTEM, which a model may not make, so they come from a server-side ledger. A
 * prefill's fields are a TRANSCRIPTION of the sentence the person just typed, going into empty
 * boxes that person is about to read, on a form they have to press **Update** on themselves.
 */

/**
 * The three form keys. A closed union, not a string: an unknown key from the wire resolves to
 * `null` through `supportSettingsFor` and renders no chip at all.
 */
export type SupportSettingsKey = "create-user" | "create-resource" | "create-filter";

/**
 * Which page consumes the prefill.
 *
 * ⚠️ IT IS NOT THE SAME FACT AS `hostScreen`, even though today it is derivable from it. The
 * screen is where the chip NAVIGATES; the consumer is which hook claims the intent. Resource
 * Settings hosts two of these forms on two different tabs of one page, so the day a third
 * settings page appears the two facts stop agreeing — and the failure mode of getting it wrong
 * is a chip that navigates perfectly and then does nothing at all.
 */
export type SupportSettingsConsumer = "UsersPage" | "OperationsPage";

export interface SupportSettingsDescriptor {
  /** Stable key. This is what crosses the wire. */
  readonly key: SupportSettingsKey;

  /**
   * Which screen hosts the form, as a key from `utility/routes.ts`.
   *
   * ⚠️ BOTH ARE `manager` SCREENS, AND THAT IS THE ACCESS GATE. The chip is checked with
   * `canReachScreen` before it renders — the second of two independent defences, the first
   * being the Function App stripping the marker for anyone who cannot reach the screen.
   */
  readonly hostScreen: "settings-users" | "settings-operations";

  /** The page whose hook claims the intent. See `SupportSettingsConsumer`. */
  readonly consumer: SupportSettingsConsumer;

  /**
   * The chip's label.
   *
   * ⚠️ IT PROMISES ONLY WHAT HAPPENS. "Fill in a new user on User Settings" is true; "Create a
   * user" is not, and somebody who presses a button labelled that will believe the user exists.
   * The distinction is the entire feature.
   */
  readonly label: string;

  /**
   * What the panel puts in the thread once the chip is used.
   *
   * ⚠️ NOT DECORATION, AND NOT A TOOLTIP. A form that fills itself in reads as something having
   * been submitted — the same reason the thumbs-down notice exists and the Manager-view flip is
   * announced. It goes where the user is already reading, and it names the button, because
   * "press Save" would be wrong: the button reads **Update** the moment the form is dirty, and
   * a prefill always makes it dirty (`UsersPage.tsx:1589`, `OperationsPage.tsx:1257`).
   *
   * The wording follows the app's own vocabulary, which already says *"User \"X\" added (not yet
   * saved)."* on the very screen this lands on.
   */
  readonly unsavedNotice: string;

  /**
   * Every field this form accepts, restated from the Function App's schema.
   *
   * Carried for the contract test and to make the chip's own `title` honest about what was
   * filled in. The page hooks read the values they know about and ignore the rest — an extra
   * field from a newer Function App must cost nothing.
   */
  readonly fields: readonly string[];
}

/**
 * WHAT IS DELIBERATELY NOT HERE, AND WHY.
 *
 * • A **User code** field. The page generates it (`generateUserCode()`, `UsersPage.tsx:748`) and
 *   there is no box for it on the form at all, so a seeded one could not be seen or corrected —
 *   it would surface only as a duplicate-code refusal at save time, on a value nobody chose.
 *
 * • A **password** field. It is not a credential: `password` is mapped to `ResourceAlias`, the
 *   TENANT DISCRIMINATOR on `dbo.Users`, and `onAddUser` inherits it from the users already
 *   loaded (`UsersPage.tsx:1171-1174`) precisely so a new user lands in the right organisation.
 *   A value arriving from a chat message would file them under another one. This is the worst
 *   thing this family could do and the reason the field list is an allowlist on BOTH sides.
 *
 * • Anything that EDITS. Every key here creates; none of them selects an existing user,
 *   resource or filter and changes it. An edit prefill would have to name a record, which means
 *   the marker would have to carry an identifier for something that already exists — and the
 *   whole safety argument for a transcription is that it asserts nothing about what is there.
 *
 * • A **delete**. Obviously; recorded here because "the list is short" is not a reason and
 *   somebody should have to write down why before adding one.
 */
const FORMS: readonly SupportSettingsDescriptor[] = Object.freeze([
  {
    key: "create-user",
    hostScreen: "settings-users",
    consumer: "UsersPage",
    label: "Fill in a new user on User Settings",
    unsavedNotice:
      "I've filled in what you gave me on User Settings. Nothing is saved yet — check it over " +
      "and press Update.",
    fields: Object.freeze([
      "name",
      "email",
      "warehouse",
      "manager",
      "shopfloor",
      "disabled",
    ]),
  },
  {
    key: "create-resource",
    hostScreen: "settings-operations",
    consumer: "OperationsPage",
    label: "Fill in a new resource on Resource Settings",
    unsavedNotice:
      "I've filled in what you gave me on Resource Settings. Nothing is saved yet — check it " +
      "over and press Update.",
    fields: Object.freeze(["code", "sortOrder"]),
  },
  {
    key: "create-filter",
    hostScreen: "settings-operations",
    consumer: "OperationsPage",
    label: "Fill in a new resource filter on Resource Settings",
    unsavedNotice:
      "I've filled in what you gave me on Resource Settings. Nothing is saved yet — check it " +
      "over and press Update.",
    fields: Object.freeze(["name", "description"]),
  },
]);

/** Every form key, for the contract test and for exhaustiveness checks. */
export const SUPPORT_SETTINGS_KEYS: readonly SupportSettingsKey[] = Object.freeze(
  FORMS.map((f) => f.key)
);

const BY_KEY: ReadonlyMap<string, SupportSettingsDescriptor> = new Map(
  FORMS.map((f) => [f.key, f])
);

/**
 * Look a form up by key. An unknown key — an older or newer Function App, or a model that
 * invented one — returns `null`. It never throws and never guesses: a chip that cannot be
 * resolved is a chip that is not rendered.
 */
export const supportSettingsFor = (
  key: string | null | undefined
): SupportSettingsDescriptor | null => (key && BY_KEY.get(key)) || null;

/**
 * Whether a key is one of the three, as a type guard.
 *
 * Used by the bus, which will not record an intent it cannot describe — an intent with a key
 * nothing can claim would sit in the module until its TTL expired.
 */
export const isSupportSettingsKey = (
  key: string | null | undefined
): key is SupportSettingsKey => Boolean(key && BY_KEY.has(key));

/**
 * The values, reduced to the fields this build knows about.
 *
 * ⚠️ A SECOND, INDEPENDENT ALLOWLIST. The Function App already drops undeclared fields against
 * its own schema, and this drops them again against this one — the same "neither side may
 * assume the other ran" rule the access gate follows. It matters more here than it looks: these
 * values are spread into a form's state, so a field this build does not expect is a key written
 * into `FormState` by name.
 *
 * Values are left exactly as they arrived. Trimming or reinterpreting them here would be a
 * second opinion about text the server has already bounded, and the person is about to read it.
 */
export const supportSettingsFields = (
  descriptor: SupportSettingsDescriptor,
  fields: Readonly<Record<string, string>> | null | undefined
): Record<string, string> => {
  const allowed: Record<string, string> = {};

  descriptor.fields.forEach((name) => {
    const value = fields?.[name];
    if (typeof value === "string" && value.length > 0) allowed[name] = value;
  });

  return allowed;
};
